/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";
import { createService } from "../../../../graphql/mutations";
import { performUpdateService } from "../../../../graphql/service/serviceCRUD";

import {
  Backdrop,
  CircularProgress,
  Menu,
  MenuItem,
  Portal,
  Tooltip,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  BulkActionsToolbar,
  Button,
  Confirm,
  FilterButton,
  ListContextProvider,
  ListToolbar,
  TopToolbar,
  useList,
  useNotify,
} from "react-admin";
import { useFormState } from "react-final-form";

import {
  isSubscriptionDisabled,
  logError,
  logHistory,
} from "../../../../utils/appUtils";
import { ResourceType, UnitOptions } from "../../../../utils/constants";
import { deepRedirect } from "../../../../utils/utils";

import {
  CreateIcon,
  ExportIcon,
} from "../../../../components/common/iconography";

import { ServicesExportDialog } from "./ServicesExportDialog";
import {
  composeNewService,
  fetchServices,
  getDefaultTaxRate,
} from "../../services/ServiceUtils";
import {
  BulkDeleteServicesButton,
  exportServicesToCsv,
  getListFilters,
  PaginationToolbar,
  ServicesRatesDatagrid,
} from "../../services/ServicesTabShared";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: theme.palette.common.white,
  },
}));

const exportList = (services, reloadyard) => {
  const fileNamePrefix = reloadyard?.name || "services";
  exportServicesToCsv(services, fileNamePrefix);
};

const ExportServicesMenuButton = ({
  reloadyard,
  services = [],
  selectedServices = [],
  disabled,
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const defaultTaxRate = getDefaultTaxRate(reloadyard);
  const [anchorEl, setAnchorEl] = useState(null);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const exportServices =
    selectedServices && selectedServices.length > 0
      ? selectedServices
      : services;

  const handleCSV = () => {
    exportList(exportServices, reloadyard);
    handleClose();
  };

  const handleToAccounts = () => {
    setAccountsOpen(true);
    handleClose();
  };

  const exportToAccounts = async (selectedAccounts) => {
    try {
      setIsSaving(true);
      for (const account of selectedAccounts) {
        const existingServices = await fetchServices(account.id, true);
        const oldServicesMap = existingServices?.length
          ? existingServices.reduce((acc, s) => {
              acc[s.name] = s;
              return acc;
            }, {})
          : {};

        const results = await Promise.allSettled(
          exportServices.map((item) => {
            const oldService = oldServicesMap[item.name];
            if (oldService) {
              const input = { ...oldService };
              input.description = item.description || "";
              input.status = item.status;
              input.taxRate = item.taxRate || defaultTaxRate;
              UnitOptions.forEach((unit) => {
                input[unit.column] = item[unit.column] || 0;
              });
              return performUpdateService(input);
            }
            const input = composeNewService(item, account);
            return API.graphql(graphqlOperation(createService, { input }));
          }),
        );

        const resError = results.find((itm) => itm.status === "rejected");
        if (resError) throw resError.reason;

        const newServices = [];
        const updatedServiceMap = {};
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            const payload = result.value?.data;
            if (payload?.createService) newServices.push(payload.createService);
            if (payload?.updateService)
              updatedServiceMap[payload.updateService.id] =
                payload.updateService;
          }
        });

        for (const service of newServices) {
          const historyNote = {
            parentId: service.id,
            parentType: ResourceType.SERVICE,
            text: "Service Created.",
          };
          logHistory(historyNote);
        }
        for (const service of Object.values(updatedServiceMap)) {
          const historyNote = {
            parentId: service.id,
            parentType: ResourceType.SERVICE,
            text: "Services bulk update.",
          };
          logHistory(historyNote);
        }
      }

      const count = selectedAccounts.length;
      notify(
        `Services exported to ${count} account${count === 1 ? "" : "s"}.`,
        "success",
      );
    } catch (error) {
      logError("services:ServicesTab:exportToAccounts", error, notify);
    } finally {
      setIsSaving(false);
      setAccountsOpen(false);
    }
  };

  if (disabled)
    return (
      <Tooltip title="No services selected" arrow placement="left">
        <span>
          <Button label="Export" disabled>
            <ExportIcon />
          </Button>
        </span>
      </Tooltip>
    );

  return (
    <>
      <Button
        label="Export"
        onClick={handleClick}
        color="primary"
        disabled={disabled}
      >
        <ExportIcon />
      </Button>
      <Menu
        id="export-services-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        getContentAnchorEl={null}
      >
        <MenuItem onClick={handleToAccounts}>To Accounts</MenuItem>
        <MenuItem onClick={handleCSV}>CSV</MenuItem>
      </Menu>

      {accountsOpen && (
        <ServicesExportDialog
          open={accountsOpen}
          onClose={() => setAccountsOpen(false)}
          onSubmit={exportToAccounts}
          exportServices={exportServices}
        />
      )}

      <Portal>
        <Backdrop open={isSaving} className={classes.backdrop}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Portal>
    </>
  );
};

const ListActions = (props) => {
  const {
    reloadyard,
    allServices,
    selectedServices,
    backPath,
    history,
    formState,
    openConfirm,
    xsDown,
  } = props;
  // legacy import dialog state removed in favor of ImportServicesMenuButton

  const handleCreate = () => {
    if (formState.dirty) openConfirm("/services/create");
    else deepRedirect(history, "/services/create", backPath, { reloadyard });
  };

  return (
    <TopToolbar>
      <FilterButton />
      <Button
        label="Create"
        color="primary"
        onClick={handleCreate}
        disabled={isSubscriptionDisabled()}
      >
        <CreateIcon />
      </Button>
      {!xsDown && (
        <ExportServicesMenuButton
          reloadyard={reloadyard}
          services={allServices}
          selectedServices={selectedServices}
          disabled={!selectedServices?.length}
        />
      )}
    </TopToolbar>
  );
};

// Direct fetch allows account to load faster.
export const fetchTabData = async (
  setAllServices,
  setServices,
  setLoading,
  notify,
  reloadyard,
) => {
  try {
    setLoading && setLoading(true);
    const allItems = await fetchServices(reloadyard.id, true);
    setAllServices(allItems);
    setServices && setServices(allItems);
  } catch (error) {
    logError("ServicesTab:fetchTabData", error, notify);
  } finally {
    setLoading && setLoading(false);
  }
};

export const ServicesTab = ({
  reloadyard,
  backPath,
  allServices,
  setAllServices,
  ...props
}) => {
  const notify = useNotify();
  const history = useHistory();
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPath, setConfirmPath] = useState("");
  const formState = useFormState();
  const defaultSort = { field: "name", order: "ASC" };
  // const fixedFilter = { active: "true" };

  // memorize perPage settings
  const sessionPerPage = sessionStorage.getItem(`ServicesTab`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10,
  );

  useEffect(() => {
    fetchTabData(setAllServices, setServices, setLoading, notify, reloadyard);
  }, []);

  // Refresh callback for upload dialog
  const handleRefresh = () => {
    fetchTabData(setAllServices, setServices, null, notify, reloadyard);
  };

  const listContext = useList({
    ...props,
    data: services || [],
    ids: services?.map(({ id }) => id) || [],
    loading: loading,
    loaded: !loading || allServices?.length > 0, // renders loading bars if false
    // filter: fixedFilter, // filtered in query
    sort: defaultSort,
    perPage: defaultPerPage,
    basePath: "/services",
    resource: "services",
  });

  const selectedServices = useMemo(() => {
    const selectedIds = Array.isArray(listContext.selectedIds)
      ? listContext.selectedIds
      : [];
    if (!selectedIds.length) return [];
    const byId = new Map(allServices?.map((service) => [service.id, service]));
    return selectedIds.map((id) => byId.get(id)).filter(Boolean);
  }, [allServices, listContext.selectedIds]);

  useEffect(() => {
    if (listContext?.perPage) {
      sessionStorage.setItem(`ServicesTab`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));

      // When perPage changes go back to page 1
      if (listContext.page === 1) return;
      listContext.setPage?.(1);
    }
  }, [listContext?.perPage]);

  const openConfirm = (redirectPath) => {
    setConfirmPath(redirectPath);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!confirmPath) return;
    deepRedirect(history, confirmPath, backPath, { reloadyard });
  };

  return (
    <ListContextProvider value={listContext}>
      <ListToolbar
        {...props}
        filters={getListFilters(allServices, setServices)}
        actions={
          <ListActions
            reloadyard={reloadyard}
            history={history}
            allServices={allServices}
            selectedServices={selectedServices}
            formState={formState}
            openConfirm={openConfirm}
            xsDown={xsDown}
            handleRefresh={handleRefresh}
          />
        }
      />
      <BulkActionsToolbar>
        <BulkDeleteServicesButton
          allServices={allServices}
          isYardTemplate
          handleRefresh={handleRefresh}
        />
      </BulkActionsToolbar>
      <ServicesRatesDatagrid
        allServices={allServices}
        xsDown={xsDown}
        enableSelection
        onOpenService={(id) =>
          deepRedirect(history, `/services/${id}`, backPath, { reloadyard })
        }
        onEdit={(record) => {
          if (formState.dirty) openConfirm(`/services/${record.id}`);
          else
            deepRedirect(history, `/services/${record.id}`, backPath, {
              reloadyard,
            });
        }}
      />
      <PaginationToolbar {...props} />
      <Confirm
        isOpen={confirmOpen}
        title="Save Confirmation"
        content="You have un-saved changes. If you continue, all changes will be lost. Proceed?"
        onConfirm={() => handleConfirm()}
        onClose={() => setConfirmOpen(false)}
      />
    </ListContextProvider>
  );
};
